import { useEffect } from 'react';
import { create } from 'zustand';

export const NEVER_ASK_STORAGE_KEY = 'mp.dev.platform.notifications.never-ask';
export const SNOOZED_UNTIL_STORAGE_KEY = 'mp.dev.platform.notifications.snoozed-until';

export const getBrowserNotificationPermission = (): NotificationPermission | 'unsupported' =>
  window.isSecureContext && 'Notification' in window && typeof window.Notification.requestPermission === 'function'
    ? window.Notification.permission
    : 'unsupported';

type PermissionRequestStatus = 'idle' | 'pending' | 'unanswered';

// Retain choices for this page session, even when browser storage is unavailable.
let neverAskUntilReload = false;
let snoozedUntilReload = 0;
let pendingRequest: object | undefined;

const clearSuppression = (): void => {
  neverAskUntilReload = false;
  snoozedUntilReload = 0;
  try {
    localStorage.removeItem(NEVER_ASK_STORAGE_KEY);
    localStorage.removeItem(SNOOZED_UNTIL_STORAGE_KEY);
  } catch {
    // Browser permission remains authoritative when storage is unavailable.
  }
};

const snoozeForToday = (): void => {
  const tomorrow = new Date();
  tomorrow.setHours(24, 0, 0, 0);
  snoozedUntilReload = tomorrow.getTime();
  try {
    localStorage.setItem(SNOOZED_UNTIL_STORAGE_KEY, String(tomorrow.getTime()));
  } catch {
    // The snooze remains effective in memory.
  }
};

const readNeverAskPreference = (): boolean => {
  if (neverAskUntilReload) return true;
  try {
    return localStorage.getItem(NEVER_ASK_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
};

const isAutomaticallySuppressed = (): boolean => {
  if (readNeverAskPreference()) return true;
  let snoozedUntil = snoozedUntilReload;
  try {
    const storedSnoozedUntil = Number(localStorage.getItem(SNOOZED_UNTIL_STORAGE_KEY));
    if (Number.isFinite(storedSnoozedUntil)) snoozedUntil = Math.max(snoozedUntil, storedSnoozedUntil);
  } catch {
    // Use the in-memory choice when browser storage is unavailable.
  }
  return snoozedUntil > Date.now();
};

interface BrowserNotificationPermissionState {
  permission: NotificationPermission | 'unsupported';
  reason: string | undefined;
  requestStatus: PermissionRequestStatus;
  neverAsk: boolean;
  updatePermission: (permission: NotificationPermission | 'unsupported') => void;
  refreshPermission: () => void;
}

const usePermissionStore = create<BrowserNotificationPermissionState>((set, get) => ({
  permission: getBrowserNotificationPermission(),
  reason: undefined,
  requestStatus: 'idle',
  neverAsk: readNeverAskPreference(),
  updatePermission: (permission) => {
    if (permission === 'granted' && get().permission !== 'granted') clearSuppression();
    if (permission !== 'default') pendingRequest = undefined;
    set({ permission, requestStatus: permission === 'default' ? get().requestStatus : 'idle', neverAsk: readNeverAskPreference() });
  },
  refreshPermission: () => get().updatePermission(getBrowserNotificationPermission()),
}));

export const useBrowserNotificationPermission = () => {
  const permission = usePermissionStore((state) => state.permission);
  const refreshPermission = usePermissionStore((state) => state.refreshPermission);
  useEffect(() => {
    refreshPermission();
    window.addEventListener('focus', refreshPermission);
    document.addEventListener('visibilitychange', refreshPermission);
    // Browser chrome approval does not resolve requestPermission in every browser.
    const interval = window.setInterval(refreshPermission, 1000);
    return () => {
      window.removeEventListener('focus', refreshPermission);
      document.removeEventListener('visibilitychange', refreshPermission);
      window.clearInterval(interval);
    };
  }, [refreshPermission]);
  return permission;
};

export const usePermissionReason = () => usePermissionStore((state) => state.reason);

export const usePermissionRequestStatus = () => usePermissionStore((state) => state.requestStatus);

export const useBrowserNotificationOptOut = () => usePermissionStore((state) => state.neverAsk);

export const closeBrowserNotificationPermissionDialog = (): void => {
  if (usePermissionStore.getState().reason !== undefined && getBrowserNotificationPermission() === 'default') snoozeForToday();
  usePermissionStore.setState({ reason: undefined });
};

export const resetBrowserNotificationOptOut = (): void => {
  neverAskUntilReload = false;
  try {
    localStorage.removeItem(NEVER_ASK_STORAGE_KEY);
  } catch {
    // Reset the in-memory choice when browser storage is unavailable.
  }
  usePermissionStore.setState({ neverAsk: false });
};

export const neverAskForBrowserNotifications = (): void => {
  neverAskUntilReload = true;
  try {
    localStorage.setItem(NEVER_ASK_STORAGE_KEY, 'true');
  } catch {
    // The opt-out remains effective in memory.
  }
  usePermissionStore.setState({ reason: undefined, neverAsk: true });
};

// Invoke directly from the dialog's Yes handler to preserve the user gesture.
export const requestBrowserNotificationPermission = (): void => {
  usePermissionStore.getState().refreshPermission();
  const { reason, requestStatus, permission } = usePermissionStore.getState();
  if (reason === undefined || requestStatus !== 'idle' || permission !== 'default' || pendingRequest !== undefined) {
    return;
  }

  const request = {};
  pendingRequest = request;
  usePermissionStore.setState({ requestStatus: 'pending' });
  snoozeForToday();

  const settleRequest = () => {
    if (pendingRequest !== request) return;
    pendingRequest = undefined;
    usePermissionStore.getState().refreshPermission();
    usePermissionStore.setState({ requestStatus: getBrowserNotificationPermission() === 'default' ? 'unanswered' : 'idle' });
  };

  try {
    void window.Notification.requestPermission().then(settleRequest, settleRequest);
  } catch {
    settleRequest();
  }
};

// Settings explicitly opens this dialog even when automatic prompts were disabled.
export const openBrowserNotificationPermissionDialog = (reason: string): void => {
  usePermissionStore.getState().refreshPermission();
  if (getBrowserNotificationPermission() === 'granted' || usePermissionStore.getState().reason !== undefined) {
    return;
  }
  usePermissionStore.setState({ reason, requestStatus: pendingRequest === undefined ? 'idle' : 'pending' });
};

export const askForBrowserNotificationPermission = (reason: string): void => {
  if (getBrowserNotificationPermission() !== 'default' || isAutomaticallySuppressed()) return;
  openBrowserNotificationPermissionDialog(reason);
};
